import { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  CircularProgress,
  Switch,
  Tooltip,
  FormControlLabel,
  Tabs,
  Tab,
} from '@mui/material';
import Modal from 'shared/Modal';
import { ModalPropsToOverride } from 'components/DynamicModalProvider/types';
import { useSelector } from 'react-redux';
import { selectAuthAccount } from 'AuthModule/selectors';
import { selectTeamHash } from 'TeamModule/selectors';
import { useUpdateConnectionInfoMutation } from 'IntegrationModule/api/queries/useUpdateConnectionInfoMutation';
import { useFetchReplicaCredentialsMutation } from 'IntegrationModule/api/queries/useFetchReplicaCredentialsMutation';
import { useProvisionReplicaDatabaseMutation } from 'IntegrationModule/api/queries/useProvisionReplicaDatabaseMutation';
import { useProvisionBakIntegrationMutation } from 'IntegrationModule/api/queries/useProvisionBakIntegrationMutation';
import { INTEGRATION_SERVICE_NAMES, IntegrationAgentNames } from '@constants';

interface EditConnectionInfoModalProps extends ModalPropsToOverride {
  targetServiceId: number;
  teamId: number;
  serviceName: string;
  isBakIntegration?: boolean;
  tenant?: string;
  realmId?: string;
}

const REPLICATION_TAB = 0;
const BAK_TAB = 1;

// Hosted target services that have a corresponding BAK-capable agent.
// `serviceName` (a.k.a. `targetService`) on this modal is the hosted variant
// (e.g. "deltek-ajera-hosted"), but POST /bak-integrations expects the agent
// name (e.g. "ajera-agent"). Keep this in sync with bakIntegrationItems in
// BakIntegrationProvisionModal.
const HOSTED_TO_BAK_AGENT: Record<string, IntegrationAgentNames> = {
  [INTEGRATION_SERVICE_NAMES.DELTEK_AJERA_HOSTED]: IntegrationAgentNames.DeltekAjeraAgent,
  [INTEGRATION_SERVICE_NAMES.BST_HOSTED]: IntegrationAgentNames.BstAgent,
  [INTEGRATION_SERVICE_NAMES.DELTEK_VISION_HOSTED]: IntegrationAgentNames.DeltekVisionAgent,
};

type ReplicationTabFieldsPropsType = {
  isHostedService: boolean;
  isAnyLoading: boolean;
  useProvisionedCredentials: boolean;
  setUseProvisionedCredentials: (checked: boolean) => void;
  credentialsExist: ReturnType<typeof useFetchReplicaCredentialsMutation>['credentialsExist'];
  isFetchingReplicaCredentials: boolean;
  isProvisioningReplicaDatabase: boolean;
  provisionResult: ReturnType<typeof useProvisionReplicaDatabaseMutation>['provisionResult'];
  onProvisionReplicaDatabase: () => void;
  tenant?: string;
  realmId?: string;
  dbHostName: string;
  setDbHostName: (value: string) => void;
  dbName: string;
  setDbName: (value: string) => void;
  dbUsername: string;
  setDbUsername: (value: string) => void;
  dbPassword: string;
  setDbPassword: (value: string) => void;
  schemaName: string;
  setSchemaName: (value: string) => void;
};

const ReplicationTabFields = ({
  isHostedService,
  isAnyLoading,
  useProvisionedCredentials,
  setUseProvisionedCredentials,
  credentialsExist,
  isFetchingReplicaCredentials,
  isProvisioningReplicaDatabase,
  provisionResult,
  onProvisionReplicaDatabase,
  tenant,
  realmId,
  dbHostName,
  setDbHostName,
  dbName,
  setDbName,
  dbUsername,
  setDbUsername,
  dbPassword,
  setDbPassword,
  schemaName,
  setSchemaName,
}: ReplicationTabFieldsPropsType) => (
  <>
    {isHostedService && (
      <>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={useProvisionedCredentials}
                onChange={(e) => setUseProvisionedCredentials(e.target.checked)}
                disabled={credentialsExist !== true || isFetchingReplicaCredentials}
              />
            }
            label={
              isFetchingReplicaCredentials
                ? 'Checking credentials...'
                : 'Use Provisioned Credentials'
            }
          />
          <Tooltip title={credentialsExist === true ? 'Database is already provisioned' : ''}>
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={onProvisionReplicaDatabase}
                disabled={isAnyLoading || !tenant || !realmId || credentialsExist === true}
              >
                {isProvisioningReplicaDatabase ? (
                  <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                ) : null}
                Provision Replica DB
              </Button>
            </span>
          </Tooltip>
        </Box>

        {useProvisionedCredentials && (
          <Typography
            variant="body2"
            sx={{
              color: 'success.main',
            }}
          >
            Database fields are disabled. Provisioned credentials will be used on
            &ldquo;Update&rdquo;.
          </Typography>
        )}

        {credentialsExist === false && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            No existing credentials found. You may provision a new replica database or enter
            credentials manually for customer hosted replicas.
          </Typography>
        )}

        {provisionResult && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'action.hover',
              fontSize: '0.85rem',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Provisioned: {provisionResult.databaseName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              User: {provisionResult.replicationUser} · Status: {provisionResult.status}
            </Typography>
          </Box>
        )}
      </>
    )}

    <TextField
      label="DB Host Name"
      value={dbHostName}
      onChange={(e) => setDbHostName(e.target.value)}
      fullWidth
      size="small"
      disabled={isAnyLoading || useProvisionedCredentials}
    />

    <TextField
      label="Database Name"
      value={dbName}
      onChange={(e) => setDbName(e.target.value)}
      fullWidth
      size="small"
      disabled={isAnyLoading || useProvisionedCredentials}
    />

    <TextField
      label="DB Username"
      value={dbUsername}
      onChange={(e) => setDbUsername(e.target.value)}
      fullWidth
      size="small"
      disabled={isAnyLoading || useProvisionedCredentials}
    />

    <TextField
      label="DB Password"
      type="password"
      value={dbPassword}
      onChange={(e) => setDbPassword(e.target.value)}
      fullWidth
      size="small"
      autoComplete="new-password"
      disabled={isAnyLoading || useProvisionedCredentials}
    />

    <TextField
      label="Schema Name"
      value={schemaName}
      onChange={(e) => setSchemaName(e.target.value)}
      fullWidth
      size="small"
      disabled={isAnyLoading || useProvisionedCredentials}
    />
  </>
);

type BakTabFieldsPropsType = {
  isAnyLoading: boolean;
  bakAgentName?: IntegrationAgentNames;
  realmId?: string;
  s3BucketName: string;
  setS3BucketName: (value: string) => void;
  schemaName: string;
  setSchemaName: (value: string) => void;
};

const BakTabFields = ({
  isAnyLoading,
  bakAgentName,
  realmId,
  s3BucketName,
  setS3BucketName,
  schemaName,
  setSchemaName,
}: BakTabFieldsPropsType) => (
  <>
    {!bakAgentName && (
      <Typography
        variant="body2"
        sx={{
          color: 'error.main',
        }}
      >
        .Bak integrations are not supported for this service.
      </Typography>
    )}
    {bakAgentName && !realmId && (
      <Typography
        variant="body2"
        sx={{
          color: 'error.main',
        }}
      >
        Realm context is missing — open this integration from a realm-scoped page to update its .Bak
        settings.
      </Typography>
    )}

    <TextField
      label="S3 Bucket Name"
      value={s3BucketName}
      onChange={(e) => setS3BucketName(e.target.value)}
      fullWidth
      size="small"
      disabled={isAnyLoading || !bakAgentName || !realmId}
    />

    <TextField
      label="DB Dump Schema Name"
      value={schemaName}
      onChange={(e) => setSchemaName(e.target.value)}
      fullWidth
      size="small"
      disabled={isAnyLoading || !bakAgentName || !realmId}
    />
  </>
);

type SubmitEligibilityInput = {
  isBakTab: boolean;
  s3BucketName: string;
  realmId?: string;
  bakAgentName?: IntegrationAgentNames;
  useProvisionedCredentials: boolean;
  dbHostName: string;
  dbName: string;
  dbUsername: string;
  dbPassword: string;
  schemaName: string;
};

/** The .Bak tab needs a bucket plus realm/agent context; replication needs any one field. */
const deriveSubmitEligibility = ({
  isBakTab,
  s3BucketName,
  realmId,
  bakAgentName,
  useProvisionedCredentials,
  dbHostName,
  dbName,
  dbUsername,
  dbPassword,
  schemaName,
}: SubmitEligibilityInput) => {
  const canSubmitBak = Boolean(s3BucketName) && Boolean(realmId) && Boolean(bakAgentName);
  const hasAtLeastOneField = isBakTab
    ? canSubmitBak
    : useProvisionedCredentials || dbHostName || dbName || dbUsername || dbPassword || schemaName;

  return { canSubmitBak, hasAtLeastOneField };
};

const EditConnectionInfoModal = ({
  isOpen,
  toggle,
  targetServiceId,
  teamId,
  serviceName,
  isBakIntegration: initialIsBakIntegration = false,
  tenant,
  realmId,
}: EditConnectionInfoModalProps) => {
  const account = useSelector(selectAuthAccount);
  const teamHash = useSelector(selectTeamHash);
  const teamName = teamHash[teamId]?.name || '';
  const galaxyUserId = account?.id?.toString() || '0';
  const galaxyUserEmail = account?.email || '';

  const [activeTab, setActiveTab] = useState(initialIsBakIntegration ? BAK_TAB : REPLICATION_TAB);

  // Replication fields
  const [dbHostName, setDbHostName] = useState('');
  const [dbName, setDbName] = useState('');
  const [dbUsername, setDbUsername] = useState('');
  const [dbPassword, setDbPassword] = useState('');
  const [schemaName, setSchemaName] = useState('');
  const [useProvisionedCredentials, setUseProvisionedCredentials] = useState(false);

  // .Bak Integration fields
  const [s3BucketName, setS3BucketName] = useState('');

  const { updateConnectionInfo, updateConnectionInfoAsync, isUpdatingConnectionInfo } =
    useUpdateConnectionInfoMutation();
  const { fetchReplicaCredentialsAsync, isFetchingReplicaCredentials, credentialsExist } =
    useFetchReplicaCredentialsMutation();
  const { provisionReplicaDatabaseAsync, isProvisioningReplicaDatabase, provisionResult } =
    useProvisionReplicaDatabaseMutation();
  const { provisionBakIntegrationAsync, isProvisioningBakIntegration } =
    useProvisionBakIntegrationMutation();

  const isHostedService = serviceName.includes('hosted');
  const isBakTab = activeTab === BAK_TAB;
  const isAnyLoading =
    isUpdatingConnectionInfo ||
    isFetchingReplicaCredentials ||
    isProvisioningReplicaDatabase ||
    isProvisioningBakIntegration;

  const bakAgentName = HOSTED_TO_BAK_AGENT[serviceName];
  const { hasAtLeastOneField } = deriveSubmitEligibility({
    isBakTab,
    s3BucketName,
    realmId,
    bakAgentName,
    useProvisionedCredentials,
    dbHostName,
    dbName,
    dbUsername,
    dbPassword,
    schemaName,
  });

  /** Auto-check credentials when modal opens for hosted services */
  useEffect(() => {
    if (!isHostedService || !serviceName || !realmId) return;

    let isCurrent = true;

    fetchReplicaCredentialsAsync({
      targetServiceId,
      tenant,
      realmId,
    })
      .then((response) => {
        if (!isCurrent) return;
        if (response.data.exists) {
          setUseProvisionedCredentials(true);
          setDbHostName('');
          setDbName('');
          setDbUsername('');
          setDbPassword('');
        }
      })
      .catch(() => {
        // Error notification is shown by the mutation hook
      });

    return () => {
      isCurrent = false;
    };
  }, [
    serviceName,
    teamId,
    tenant,
    realmId,
    isHostedService,
    fetchReplicaCredentialsAsync,
    targetServiceId,
  ]);

  /** Provision a replica database in Azure, then re-check credentials */
  const handleProvisionReplicaDatabase = async () => {
    if (!serviceName || !realmId || !tenant) return;
    try {
      await provisionReplicaDatabaseAsync({
        tenant,
        realmId,
        teamId,
        targetServiceId,
        service: serviceName,
        teamName,
      });
      const response = await fetchReplicaCredentialsAsync({
        targetServiceId,
        tenant,
        realmId,
      });
      if (response.data.exists) {
        setUseProvisionedCredentials(true);
        setDbHostName('');
        setDbName('');
        setDbUsername('');
        setDbPassword('');
      }
    } catch {
      // Error notification is shown by the mutation hooks
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasAtLeastOneField || !galaxyUserId) return;

    if (isBakTab) {
      // canSubmitBak (gating hasAtLeastOneField above) guarantees realmId and
      // bakAgentName are set, but narrow for TS.
      if (!realmId || !bakAgentName) return;

      try {
        // Run sequentially: only provision the .Bak integration record after
        // the connection-info update succeeds, so a failed update doesn't
        // leave behind a half-applied .Bak record.
        await updateConnectionInfoAsync({
          targetServiceId,
          galaxyUserId,
          galaxyUserEmail,
          bakIntegration: true,
          s3BucketName,
          ...(schemaName && { schemaName }),
          tenant,
          realmId,
        });

        await provisionBakIntegrationAsync({
          teamName,
          integrationId: targetServiceId,
          realmId,
          s3BucketName,
          serviceName: bakAgentName,
          tenant,
        });

        toggle();
      } catch {
        // Errors are surfaced via each mutation's onError toast.
      }
      return;
    }

    updateConnectionInfo(
      {
        targetServiceId,
        galaxyUserId,
        galaxyUserEmail,
        useProvisionedCredentials: useProvisionedCredentials || undefined,
        ...(!useProvisionedCredentials && {
          ...(dbHostName && { dbHostName }),
          ...(dbName && { dbName }),
          ...(dbUsername && { dbUsername }),
          ...(dbPassword && { dbPassword }),
          ...(schemaName && { schemaName }),
        }),
        tenant,
        realmId,
      },
      {
        onSuccess: () => {
          toggle();
        },
      },
    );
  };

  return (
    <Modal open={isOpen} onClose={isAnyLoading ? undefined : toggle} modalWidth={600}>
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Edit Connection Info
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 2,
          }}
        >
          Update connection details for integration {targetServiceId}.
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(_, newValue) => {
            setActiveTab(newValue);
            setSchemaName('');
          }}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Replication" disabled={isAnyLoading} />
          <Tab label=".Bak Integration" disabled={isAnyLoading} />
        </Tabs>

        <form onSubmit={handleSubmit}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {!isBakTab && (
              <ReplicationTabFields
                isHostedService={isHostedService}
                isAnyLoading={isAnyLoading}
                useProvisionedCredentials={useProvisionedCredentials}
                setUseProvisionedCredentials={setUseProvisionedCredentials}
                credentialsExist={credentialsExist}
                isFetchingReplicaCredentials={isFetchingReplicaCredentials}
                isProvisioningReplicaDatabase={isProvisioningReplicaDatabase}
                provisionResult={provisionResult}
                onProvisionReplicaDatabase={handleProvisionReplicaDatabase}
                tenant={tenant}
                realmId={realmId}
                dbHostName={dbHostName}
                setDbHostName={setDbHostName}
                dbName={dbName}
                setDbName={setDbName}
                dbUsername={dbUsername}
                setDbUsername={setDbUsername}
                dbPassword={dbPassword}
                setDbPassword={setDbPassword}
                schemaName={schemaName}
                setSchemaName={setSchemaName}
              />
            )}

            {isBakTab && (
              <BakTabFields
                isAnyLoading={isAnyLoading}
                bakAgentName={bakAgentName}
                realmId={realmId}
                s3BucketName={s3BucketName}
                setS3BucketName={setS3BucketName}
                schemaName={schemaName}
                setSchemaName={setSchemaName}
              />
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              mt: 3,
            }}
          >
            <Button variant="outlined" onClick={toggle} disabled={isAnyLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!hasAtLeastOneField || isAnyLoading}
            >
              {isUpdatingConnectionInfo || isProvisioningBakIntegration ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Update'
              )}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default EditConnectionInfoModal;
